<script>
import { minLength, requiredIf } from 'vuelidate/lib/validators'
import { mapActions } from 'vuex'

export default {
  name: 'ProgramsConfig',
  components: {
    ContentHeader: () => import('@/components/general/ContentHeader'),
    Tabs: () => import('@/components/general/Tabs'),
    Datatable: () => import('@/components/general/Datatable'),
    Action: () => import('@/components/general/Action'),
    FilterListSearch: () => import('@/components/general/FilterListSearch'),
    FormSection: () => import('@/components/general/FormSection'),
    Divisor: () => import('@/components/general/Divisor'),
    Icon: () => import('@/components/general/Icon'),
    Radio: () => import('@/components/general/Radio')
  },

  data () {
    return {
      formData: {
        listHighlights: [],
        ordenation: 'default'
      },

      tabLinks: [
        {
          location: { name: 'programs.index' },
          text: this.$t('global:menu.programs')
        },
        {
          location: { name: 'programs.configs' },
          text: this.$t('solutions.tabs:link.2')
        }
      ],

      orderOptions: [
        {
          label: this.$t('global:manual'),
          value: 'manual'
        },
        {
          label: this.$t('global:default'),
          value: 'default'
        }
      ],

      programsPagination: {
        query: {
          name: null,
          limit: 300
        },

        filter: {
          active: 1,
          show_on_portal: 1
        },

        order: {
          name: 'ASC'
        },

        lastPage: null,
        limit: 6,
        page: 1
      },

      isLoadingMorePrograms: false,
      programs: []
    }
  },

  validations: {
    formData: {
      listHighlights: {
        required: requiredIf(function () {
          return this.formData.ordenation === 'manual'
        }),

        minLength: minLength(4)
      }
    }
  },

  computed: {
    availablePrograms () {
      return this.programs.filter(program => !this.formData.listHighlights.find(findProgram => program.id === findProgram.id))
    }
  },

  created () {
    this.setFetching(true)

    Promise.all([
      this.fetchProgramsHighlights(),
      this.fetchProgramsData()
    ]).finally(() => {
      this.setFetching(false)
    })
  },

  methods: {
    ...mapActions([
      'setFetching',
      'setFeedback',
      'attemptGetProgramsList',
      'attemptGetListProgramsHighlights',
      'attemptUpdateProgramsHighlights',
      'attemptGetGeneralSettings'
    ]),

    fetchProgramsData (isLoadMore = false) {
      this.isLoadingMorePrograms = true

      return this.attemptGetProgramsList(this.programsPagination)
        .then((pagination) => {
          this.programsPagination.lastPage = pagination.lastPage
          this.programsPagination.page = pagination.actualPage

          if (pagination.data) {
            if (!isLoadMore) {
              this.programs = []
            }

            pagination.data.forEach(element => {
              this.programs.push({
                name: element.name,
                id: element.id,
                programSolutions: element.programSolutions,
                workload: element.workload
              })
            })
          }

          this.isLoadingMorePrograms = false

          return pagination
        })
    },

    fetchProgramsHighlights () {
      this.setFetching(true)

      this.attemptGetGeneralSettings().then(({ data }) => {
        const ordenation = data.data.find((item) => item.field === 'program_highlight_ordenation')

        this.formData.ordenation = ordenation.value ? ordenation.value : 'default'

        this.attemptGetListProgramsHighlights().then(({ data }) => {
          this.formData.listHighlights = data.data.map((highlight) => {
            return {
              active: 1,
              ...highlight.program
            }
          })

          this.setFetching(false)
        })
      })
    },

    searchProgram (search) {
      this.programsPagination.query.name = search
      this.programsPagination.page = 1
      this.programsPagination.lastPage = null
      this.programsPagination.query.only_name = true
      this.fetchProgramsData()
    },

    loadMorePrograms () {
      this.isLoadingMorePrograms = true

      if (this.programsPagination.lastPage) {
        if (this.programsPagination.lastPage > this.programsPagination.page) {
          this.programsPagination.page += 1
        } else {
          this.isLoadingMorePrograms = false

          return
        }
      }

      this.fetchProgramsData(true)
    },

    addToLinkedSolution (programId) {
      const program = this.programs.find(program => program.id === programId)

      program.active = 1

      this.formData.listHighlights.push(program)
    },

    removeLinkedSolution (programId) {
      this.formData.listHighlights = this.formData.listHighlights.filter(program => program.id !== programId)
    },

    moveUpLinkedSolution (programId) {
      const index = this.formData.listHighlights.findIndex(program => program.id === programId)

      if (index > 0) {
        const swapSolution = this.formData.listHighlights[index - 1]

        this.$set(this.formData.listHighlights, (index - 1), this.formData.listHighlights[index])
        this.$set(this.formData.listHighlights, index, swapSolution)
      }
    },

    moveDownLinkedSolution (programId) {
      const index = this.formData.listHighlights.findIndex(program => program.id === programId)

      if (index < (this.formData.listHighlights.length - 1)) {
        const swapSolution = this.formData.listHighlights[index + 1]

        this.$set(this.formData.listHighlights, (index + 1), this.formData.listHighlights[index])
        this.$set(this.formData.listHighlights, index, swapSolution)
      }
    },

    updateHighlights () {
      this.$v.$touch()
      const formatHighlights = {
        programs: [],
        ordenation: this.formData.ordenation
      }

      if (this.formData.ordenation === 'manual') {
        formatHighlights.programs = this.formData.listHighlights.map((program, index) => {
          return {
            programId: program.id,
            highlightPosition: index
          }
        })
      }

      if (!this.$v.$invalid) {
        this.attemptUpdateProgramsHighlights(formatHighlights).then(() => {
          this.setFeedback({ message: this.$t('solutions.config.highlights:feedback.success') })
        }).then(() => {
          this.fetchProgramsHighlights()
        }).catch(({ data }) => {
          if (data.error.message === 'too_many_programs') {
            this.setFeedback({ message: this.$t('programs.config.highlights:max.length.message') })

            return
          }

          this.setFeedback({ message: this.$t('solutions.config.highlights:feedback.error') })
        })
      } else {
        this.setFeedback({ message: this.$t('programs.config.highlights:min.length.message') })
      }
    }
  }
}
</script>

<template>
  <div class="main-content">
    <ContentHeader
      :title="$t('programs:header.title')"
      :description="$t('programs:header.description')"
      background="/assets/images/themes/default/png/tracks_banner.png"
    >
      <Tabs
        slot="tabs"
        dark
        :links="tabLinks"
      />
    </ContentHeader>
    <div class="center solutions-config">
      <div
        class="solutions-config_highlights"
      >
        <FormSection title="Ordenação">
          <Radio
            v-model="formData.ordenation"
            :items="orderOptions"
            horizontal
          />
        </FormSection>
        <div
          v-if="formData.ordenation === 'manual'"
          class="link-table"
        >
          <div class="linkable-items">
            <Datatable
              :is-loading-more="isLoadingMorePrograms"
              :items="availablePrograms"
              hide-horizontal-line
              has-infinite-scroll
              has-fade
              empty-message="no_result"
              @loadMore="loadMorePrograms"
            >
              <template slot="thead">
                <th
                  class="th"
                  width="300"
                >
                  {{ $t('programs.list:datatable.path.header.1') }}
                </th>
                <th
                  class="th"
                  width="40"
                />
              </template>

              <template slot="tsearch">
                <FilterListSearch
                  slot="search"
                  class="filter-search"
                  :prepend-label="$t('global:form.program-search')"
                  :hide-error-details="true"
                  @search="searchProgram"
                />
              </template>

              <template
                slot="tbody"
                slot-scope="props"
              >
                <tr
                  :key="props.index"
                  @click="addToLinkedSolution(props.item.id)"
                >
                  <td class="td td-full">
                    <span class="td-text bolder">{{ props.item.name }}</span>
                    <br>
                    <span class="td-text td-small">
                      {{ props.item.programSolutions.length }} {{ props.item.programSolutions.length === 1
                        ? 'Solução'
                        : 'Soluções' }} | {{ props.item.workload ? props.item.workload : '0h' }}
                    </span>
                  </td>
                  <td class="td td-button td-small text-right">
                    <Action
                      type="button"
                      icon="keyboard_arrow_right"
                      flat
                    />
                  </td>
                </tr>
              </template>
            </Datatable>
          </div>

          <Divisor orientation="vertical" />

          <div class="linked-items">
            <Datatable
              has-fade
              drag
              :items="formData.listHighlights"
              hide-horizontal-line
              :empty-message="$t('global:linked.items.empty')"
            >
              <template slot="thead">
                <th class="th">
                  {{ $t('programs.config.highlights:datatable.header.2') }}
                </th>
                <th
                  class="th"
                  width="40"
                />
              </template>
              <template
                slot="tbody"
                slot-scope="props"
              >
                <tr
                  v-if="props.item.active"
                  :key="props.index"
                  class="tr-draggable"
                >
                  <td class="td td-drag">
                    <Icon
                      name="drag"
                      size="is-small"
                    />
                  </td>
                  <td class="td td-full">
                    <span class="td-text bolder">{{ props.item.name }}</span>
                  </td>
                  <td class="td td-button td-small text-right">
                    <Action
                      class="order-action"
                      type="button"
                      icon="arrow_drop_down"
                      flat
                      @click="moveDownLinkedSolution(props.item.id)"
                    />
                    <Action
                      class="order-action"
                      type="button"
                      icon="arrow_drop_up"
                      flat
                      @click="moveUpLinkedSolution(props.item.id)"
                    />
                    <Action
                      class="order-action"
                      type="button"
                      icon="close"
                      flat
                      @click="removeLinkedSolution(props.item.id)"
                    />
                  </td>
                </tr>
              </template>
            </Datatable>
          </div>
        </div>
        <Action
          slot="button"
          primary
          fixed-width
          type="button"
          :text="$t('global:save')"
          class="btn-save"
          @click="updateHighlights"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">

.solutions-config {
  padding: 10px;
  display: flex;
  flex-direction: column;

  .datatable-wrapper {
    padding-top: 35px;
  }

  table .td {
    border: none;
  }

  .btn-save{
    align-self: center;
    margin-top: 15px;
  }

  .solutions-config_highlights {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 40px;

    .link-table {
      width: 100%;
    }

    .form-section {
      .form-section-title {
        text-align: center;
      }
    }
  }

}

</style>
